import { CAMPAIGN_STATUS_LABELS, MESSAGE_CHANNEL_LABELS, type CampaignStatus } from '@crm/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Plus, SendHorizontal } from 'lucide-react'
import { buttonVariants } from '@/components/ui/button'
import { Badge, Card, Skeleton, type BadgeVariant } from '@/components/ui/field'
import { campaignsQuery } from '@/features/messaging/api'
import { formatDateTime } from '@/lib/utils'
import { Route as AppRoute } from '../../_app'

export const Route = createFileRoute('/_app/campaigns/')({
  component: CampaignsPage,
})

const STATUS_TONE: Record<CampaignStatus, BadgeVariant> = {
  draft: 'muted',
  scheduled: 'default',
  sending: 'default',
  done: 'success',
  cancelled: 'muted',
}

function CampaignsPage() {
  const { me } = AppRoute.useRouteContext()
  const { data, isLoading } = useQuery(campaignsQuery)
  const items = data?.items ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Campaigns</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Targeted SMS, WhatsApp and email to filtered clients.
          </p>
        </div>
        {me.permissions.includes('campaigns:send') && (
          <Link to="/campaigns/new" className={buttonVariants()}>
            <Plus className="h-4 w-4" />
            New campaign
          </Link>
        )}
      </div>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-3/4" />
        </div>
      )}
      {data && items.length === 0 && (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <SendHorizontal className="h-8 w-8 text-[var(--color-muted-foreground)]" />
          <p className="text-sm font-medium">No campaigns yet</p>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Build one to message a filtered group of clients.
          </p>
        </Card>
      )}

      {items.length > 0 && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--color-border)] bg-[var(--color-muted)]/50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Channel</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Sent / Total</th>
                  <th className="px-4 py-2 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {items.map((campaign) => (
                  <tr
                    key={campaign.id}
                    className="border-b border-[var(--color-border)] last:border-0"
                  >
                    <td className="px-4 py-2">
                      <Link
                        to="/campaigns/$campaignId"
                        params={{ campaignId: campaign.id }}
                        className="font-medium text-[var(--color-primary)] hover:underline"
                      >
                        {campaign.name}
                      </Link>
                    </td>
                    <td className="px-4 py-2">{MESSAGE_CHANNEL_LABELS[campaign.channel]}</td>
                    <td className="px-4 py-2">
                      <Badge variant={STATUS_TONE[campaign.status]}>
                        {CAMPAIGN_STATUS_LABELS[campaign.status]}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-[var(--color-muted-foreground)]">
                      {campaign.sentCount} / {campaign.recipientCount}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-xs text-[var(--color-muted-foreground)]">
                      {formatDateTime(campaign.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}
